import React, { useState } from "react";
import { Play, Pause, X, Lock, ChevronUp, ChevronDown, Sparkles, Music } from "lucide-react";
import { useNowPlaying } from "../context/NowPlayingContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const formatTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

function TierBadge({ tier }) {
  const t = (tier || "premium").toLowerCase();
  const isPlat = t === "platinum";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
      style={
        isPlat
          ? { background: "rgba(255,213,79,0.15)", border: "1px solid rgba(255,213,79,0.55)", color: "#FFD54F" }
          : { background: "rgba(0,150,136,0.18)", border: "1px solid rgba(0,150,136,0.55)", color: "#4DB6AC" }
      }
    >
      {t.toUpperCase()}
    </span>
  );
}

export default function NowPlayingBar() {
  const { track, isPlaying, previewLeft, position, duration, togglePlay, stop } = useNowPlaying();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!track) return null;

  const userTier = (user?.tier || "free").toLowerCase();
  const showRichInfo = userTier === "premium" || userTier === "platinum";
  const isLocked = track.access_level === "preview";
  const cat = track.category_meta || null;
  const benefits = Array.isArray(cat?.benefits) ? cat.benefits : [];
  const thumb = track.thumbnail_url || cat?.thumbnail_url || null;
  const progress = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <div
      className="fixed left-0 right-0 z-40 transition-all"
      style={{
        bottom: "var(--tab-bar-height, 70px)",
        // Move the now-playing card just above the tab bar
        pointerEvents: "none",
      }}
      data-testid="now-playing-bar"
    >
      <div className="max-w-screen-sm mx-auto px-3" style={{ pointerEvents: "auto" }}>
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "rgba(8, 14, 10, 0.94)",
            border: `1px solid ${isLocked ? "rgba(255,213,79,0.55)" : "rgba(76,175,80,0.5)"}`,
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: `0 16px 36px rgba(0,0,0,0.7), 0 0 22px ${isLocked ? "rgba(255,213,79,0.18)" : "rgba(76,175,80,0.22)"}`,
          }}
        >
          {/* Compact bar */}
          <div className="flex items-center gap-3 p-3">
            {/* Thumbnail */}
            <div
              className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden relative"
              style={{
                background: "linear-gradient(135deg, rgba(76,175,80,0.25), rgba(38,166,154,0.18))",
                border: "1px solid rgba(76,175,80,0.35)",
              }}
              data-testid="np-thumbnail"
            >
              {thumb ? (
                <img src={thumb} alt="" className="w-full h-full object-cover" />
              ) : cat?.icon ? (
                <span style={{ fontSize: 26 }}>{cat.icon}</span>
              ) : (
                <Music size={18} className="text-[#4CAF50]" />
              )}
              {isPlaying && (
                <span
                  className="absolute bottom-1 right-1 w-2 h-2 rounded-full"
                  style={{ background: "#4CAF50", boxShadow: "0 0 8px #4CAF50" }}
                />
              )}
            </div>

            {/* Title row */}
            <button
              onClick={() => setExpanded((e) => !e)}
              className="flex-1 min-w-0 text-left"
              data-testid="np-expand-toggle"
            >
              <div className="flex items-center gap-1.5">
                <p className="font-semibold truncate text-sm">{track.title}</p>
                {isLocked && <Lock size={10} className="text-[#FFD54F] flex-shrink-0" />}
              </div>
              <p className="text-[11px] text-muted-soft truncate">
                {track.artist || track.frequency}
                {previewLeft != null && previewLeft > 0 && (
                  <span className="text-[#FFD54F] ml-1 font-semibold">· {previewLeft}s preview</span>
                )}
              </p>
            </button>

            {/* Controls */}
            <button
              onClick={togglePlay}
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg,#4CAF50,#66BB6A)",
                boxShadow: "0 0 12px rgba(76,175,80,0.5)",
              }}
              data-testid="np-play-pause"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={16} className="text-white" /> : <Play size={16} className="text-white ml-0.5" />}
            </button>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/5"
              data-testid="np-expand-button"
              aria-label={expanded ? "Collapse player" : "Expand player"}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
            <button
              onClick={stop}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-red-500/10"
              data-testid="np-close"
              aria-label="Close player"
            >
              <X size={15} className="text-muted-soft" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="px-3 pb-2">
            <div
              className="w-full h-1 rounded-full overflow-hidden"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progress}%`,
                  background: isLocked
                    ? "linear-gradient(90deg,#FFD54F,#FFC107)"
                    : "linear-gradient(90deg,#4CAF50,#66BB6A)",
                }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-soft mt-1">
              <span>{formatTime(position)}</span>
              <span>{track.duration || formatTime(duration)}</span>
            </div>
          </div>

          {/* Expanded panel — thumbnail, category + benefits (Premium/Platinum) */}
          {expanded && (
            <div
              className="px-4 pt-1 pb-4 border-t"
              style={{ borderColor: "rgba(255,255,255,0.06)" }}
              data-testid="np-expanded"
            >
              <div className="flex items-start gap-4 mt-3">
                {/* Big thumbnail */}
                <div
                  className="w-24 h-24 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, rgba(76,175,80,0.25), rgba(38,166,154,0.18))",
                    border: "1px solid rgba(76,175,80,0.4)",
                    boxShadow: "0 8px 22px rgba(0,0,0,0.55)",
                  }}
                  data-testid="np-big-thumb"
                >
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  ) : cat?.icon ? (
                    <span style={{ fontSize: 56 }}>{cat.icon}</span>
                  ) : (
                    <Music size={32} className="text-[#4CAF50]" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-muted-soft">Now playing</p>
                  <h3
                    className="text-lg font-extrabold leading-tight mt-0.5"
                    style={{ textShadow: "0 0 14px rgba(76,175,80,0.4)" }}
                    data-testid="np-title"
                  >
                    {track.title}
                  </h3>
                  {track.artist && <p className="text-xs text-soft mt-0.5">{track.artist}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {cat?.name && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
                        style={{ background: "rgba(76,175,80,0.15)", border: "1px solid rgba(76,175,80,0.45)", color: "#A5D6A7" }}
                        data-testid="np-category-badge"
                      >
                        {cat.icon && <span>{cat.icon}</span>} {cat.name}
                      </span>
                    )}
                    <TierBadge tier={track.tier_required || track.subscription_tier} />
                    {isLocked && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#FFD54F]">
                        <Lock size={10} /> 30s preview
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Category description + benefits — Premium/Platinum only */}
              {showRichInfo && cat ? (
                <div className="mt-4" data-testid="np-rich-info">
                  {cat.description && (
                    <p className="text-xs text-soft leading-relaxed mb-3">{cat.description}</p>
                  )}
                  {benefits.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-soft mb-2">Benefits</p>
                      <div className="flex flex-wrap gap-1.5" data-testid="np-benefits">
                        {benefits.map((b) => (
                          <span
                            key={b}
                            className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                            style={{
                              background: "linear-gradient(135deg, rgba(76,175,80,0.22), rgba(38,166,154,0.18))",
                              color: "#FFFFFF",
                              border: "1px solid rgba(76,175,80,0.4)",
                              boxShadow: "0 0 8px rgba(76,175,80,0.18)",
                            }}
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Free users: upsell hint */
                <div
                  className="mt-4 rounded-xl p-3 flex items-center gap-2"
                  style={{ background: "rgba(255,213,79,0.10)", border: "1px solid rgba(255,213,79,0.35)" }}
                  data-testid="np-free-upsell"
                >
                  <Sparkles size={14} className="text-[#FFD54F] flex-shrink-0" />
                  <p className="text-[11px] text-soft flex-1">
                    Upgrade to Premium to see this frequency's benefits & full description.
                  </p>
                  <button
                    onClick={() => navigate("/subscription")}
                    className="btn-amber text-[11px] px-3 py-1 whitespace-nowrap"
                    data-testid="np-upsell-cta"
                  >
                    Upgrade
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
